import {
  BasicButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Link,
  Modal,
  ModalTrigger,
  PrimaryButton,
  RadioGroup,
  Radio,
  Text,
  TextArea,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { JsonSchema4, JsonSchema7 } from "@jsonforms/core";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { useHandleChange } from "../../utils";

type Props = {
  skipOptions: JsonSchema4[] | JsonSchema7[] | undefined;
  otherSkipReason: string | undefined;
};

type SkipOptionsStateType = {
  otherIsSelected: boolean;
  skipReason: string;
  skipOtherReasoning: string;
};

export function IncomeVerificationSkipModal(props: Props) {
  const { skipOptions, otherSkipReason } = props;
  const { t } = useTranslation();
  const [skipValues, setSkipValues] = useState<SkipOptionsStateType>({
    otherIsSelected: false,
    skipReason: "",
    skipOtherReasoning: "",
  });
  const handleChange = useHandleChange();

  return (
    <ModalTrigger
      trigger={
        <Text color="text-base">
          <Link
            textStyle="global/heading-small"
            testID="income-verification-learn-more-modal-link"
            linkStyle="monochrome"
          >
            {t("Click here")}
          </Link>
        </Text>
      }
    >
      <Modal>
        <Dialog>
          {({ close }) => (
            <>
              <DialogHeader>{t("Skip income verification")}</DialogHeader>
              <DialogContent>
                <>
                  <Text style="global/body-primary">
                    {t(
                      "The landlord requested your income verification to better evaluate your application but you may withdraw your consent at any time."
                    )}
                  </Text>
                  <br />
                  <br />
                  <Text style="global/body-primary">
                    {t(
                      "Are you sure you want to skip? If so, select a reason for not completing the income verification step. This information will be displayed in the report."
                    )}
                  </Text>
                  <RadioGroup
                    className={styles.textPadding}
                    aria-label="income-verification-radio-group"
                    name="name"
                    onChange={(value) => {
                      setSkipValues((currValues) => ({
                        ...currValues,
                        skipReason: value,
                        otherIsSelected: value === "OTHER",
                        skipOtherReasoning:
                          value !== "OTHER"
                            ? ""
                            : skipValues.skipOtherReasoning,
                      }));
                    }}
                    isRequired
                    children={
                      skipOptions &&
                      skipOptions.map((item, index) => {
                        return (
                          <Radio
                            key={index}
                            value={item.const}
                            testID={`${item.const}-radio-selection`}
                          >
                            {item.title}
                          </Radio>
                        );
                      })
                    }
                    testID="test-id"
                  />
                  <TextArea
                    aria-label="income-verification-text-area"
                    onChange={(value) => {
                      setSkipValues((currValues) => ({
                        ...currValues,
                        skipOtherReasoning: value,
                      }));
                    }}
                    testID="income-verification-skip-text-area"
                    name="incomeVerificationOtherSkipReason"
                    maxLength={100}
                    isRequired={skipValues.otherIsSelected}
                    isDisabled={!skipValues.otherIsSelected}
                    value={skipValues.skipOtherReasoning || otherSkipReason}
                  />
                </>
              </DialogContent>
              <DialogFooter>
                <BasicButton
                  onPress={() => {
                    setSkipValues((currValues) => ({
                      ...currValues,
                      otherIsSelected: false,
                      skipReason: "",
                      skipOtherReasoning: "",
                    }));
                    close();
                  }}
                >
                  <Trans>Cancel</Trans>
                </BasicButton>
                <PrimaryButton
                  isDisabled={
                    !skipValues.skipReason ||
                    (skipValues.skipReason === "OTHER" &&
                      !skipValues.skipOtherReasoning)
                  }
                  onPress={() => {
                    handleChange("income_verification_skip", {
                      skip_verification: !!skipValues.skipReason,
                      skip_reason: skipValues.skipReason,
                      skip_other_reasoning: skipValues.skipOtherReasoning,
                    });
                    close();
                  }}
                >
                  <Trans>Continue</Trans>
                </PrimaryButton>
              </DialogFooter>
            </>
          )}
        </Dialog>
      </Modal>
    </ModalTrigger>
  );
}

const styles = {
  textPadding: css({
    marginTop: "var(--space-3)",
  }),
};
